import { Children, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Tabs } from '@arco-design/web-react';
import type { TabsProps } from '@arco-design/web-react';

export interface BccTabsProps extends TabsProps {
  /**
   * 内容区内边距档位（页面级 Tab 的内边距**只由这里出**，页面不得再自写 `.arco-tabs-content` padding）：
   * `true`（默认）= `12/16/0`；`false` = `0`（抽屉/弹窗/侧栏等紧凑场景）；
   * `'tight'` = `12/0/0`（通栏页签：内容与页头/通栏 banner 左缘对齐，如报表、AI 中台、开放平台）。
   */
  contentPadding?: boolean | 'tight';
}

/**
 * F6 兜底（bcc-tabs-unification §8.3）：全部 TabPane 均无 children 时 Arco TabContent 返回
 * null，contentPadding 开关结构性空转（脆弱模式）。dev 环境警告一次，帮助新页面避免复制该形态。
 */
function warnIfAllPanesEmpty(children: ReactNode) {
  if (!import.meta.env.DEV) return;
  const panes = Children.toArray(children).filter(
    (c) => isValidElement(c) && (c as ReactElement).type === Tabs.TabPane
  );
  if (panes.length === 0) return;
  const hasContent = panes.some((p) => {
    const pc = (p as ReactElement<{ children?: ReactNode }>).props?.children;
    return pc !== null && pc !== undefined && pc !== false && pc !== '';
  });
  if (!hasContent) {
    console.warn(
      '[BccTabs] 所有 TabPane 均无 children（空 pane + 内容外置为禁止形态，bcc-tabs-unification R1/R2）：请把内容放入对应 TabPane，内边距由 contentPadding 单源接管。'
    );
  }
}

/**
 * 全局统一 Tabs（frontend-dev-convention 配套）：
 * 自动挂载 bcc-tabs 类（components.css）——透明页头 + 主色渐变 ink + 内容区内边距 12/16/0，
 * 全站页签统一（业务蓝 ink；AI 页签可补 className="bcc-tabs-ai" 切换电光青）。
 * 用法：<BccTabs activeTab onChange>…<BccTabs.TabPane title={…}>…</BccTabs.TabPane>…
 * 紧凑场景（弹窗/抽屉/侧栏）：传 contentPadding={false}，挂载 bcc-tabs-no-pad 去除内容区内边距；
 * 通栏场景（内容要与页头/通栏 banner 左缘对齐）：传 contentPadding="tight"，挂载 bcc-tabs-tight-pad。
 */
function BccTabsFn({ className, contentPadding = true, children, ...props }: BccTabsProps) {
  warnIfAllPanesEmpty(children);
  const cls = [
    'bcc-tabs',
    contentPadding === false && 'bcc-tabs-no-pad',
    contentPadding === 'tight' && 'bcc-tabs-tight-pad',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tabs {...props} className={cls}>
      {children}
    </Tabs>
  );
}

/* TabPane 直挂（与 Arco Tabs.TabPane 相同组件） */
const BccTabs = BccTabsFn as typeof BccTabsFn & { TabPane: typeof Tabs.TabPane };
BccTabs.TabPane = Tabs.TabPane;

export default BccTabs;
export const BccTabPane = Tabs.TabPane;
